import { Box, Button, FormHelperText, MenuItem, Typography } from '@mui/material';
import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FileDropzone } from 'src/components/atoms/file-dropzone/file-dropzone';
import { getTranslationBasedOnLanguage } from 'src/utils/get-translation-based-on-language';

import { InputField } from '../../../../../components/atoms/input-field';
import FilesList from '../../../../../components/molecules/file-list/file-list';
import { CreateVisitFormValues } from '../../organisms/create-visit/schema/create-visit-validation-schema';

import { OrganizationLanguage } from '@/features/settings/api/types/gdpr';
import { VisitFinishSetting } from '@/features/visits/api/types/visits';
import { useCategories } from '@/features/visits/hooks/query/use-categories';
import {
  CreateVisitContext,
  CreateVisitEvent,
} from '@/features/visits/utils/machine/create-visit-machine';
import { useKey } from '@/hooks/use-key';

interface Props {
  context: CreateVisitContext;
  send: (event: CreateVisitEvent) => void;
}

const finishVisitOptions = [
  {
    title: 'createVisit:additionalInformation.time_ends' as const,
    value: VisitFinishSetting.TIME_ENDS,
  },
  {
    title: 'createVisit:additionalInformation.visitors_left' as const,
    value: VisitFinishSetting.VISITORS_LEFT,
  },
];

const MAX_FILE_SIZE_IN_BYTES = 15728640;

export const VisitorAdditionalInformation = ({ context, send }: Props) => {
  const [error, _] = useState<string | null>(null);

  const {
    control,
    setError,
    trigger,
    formState: { isValid, errors },
  } = useFormContext<CreateVisitFormValues>();

  const { t, i18n } = useTranslation(['common', 'createVisit']);

  const { data: categoriesData } = useCategories();

  const nextStep = async () => {
    const isFormValid = await trigger();

    if (isFormValid) {
      send({ type: 'NEXT' });
    }
  };

  useKey('Enter', nextStep);

  return (
    <div>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          gap: 2,
        }}>
        <Typography color="textPrimary" sx={{ my: 2 }} variant="h6">
          {t('common:step')} {context.step}. {t(context.content)}
        </Typography>
        <Box>
          <Controller
            control={control}
            name="categoryId"
            render={({ field, fieldState: { error } }) => (
              <InputField
                {...field}
                fullWidth
                label={t('createVisit:additionalInformation.categoryOfVisit')}
                required
                select
                helperText={error?.message}
                error={!!error?.message}>
                {categoriesData?.map(category => (
                  <MenuItem key={category.id} value={category.id}>
                    {getTranslationBasedOnLanguage(
                      category.translations,
                      i18n.language as OrganizationLanguage,
                    )}
                  </MenuItem>
                ))}
              </InputField>
            )}
          />
        </Box>
        <Box>
          <Controller
            control={control}
            name="visitFinishSetting"
            render={({ field, fieldState: { error } }) => (
              <InputField
                {...field}
                error={!!error?.message}
                fullWidth
                helperText={error?.message}
                label={t('createVisit:additionalInformation.visitFinishSetting')}
                select
                required
                sx={{
                  my: 2,
                }}>
                {finishVisitOptions?.map(finishReason => (
                  <MenuItem key={finishReason.value} value={finishReason.value}>
                    {t(finishReason.title)}
                  </MenuItem>
                ))}
              </InputField>
            )}
          />
          <Controller
            control={control}
            name="description"
            render={({ field, fieldState: { error } }) => (
              <InputField
                {...field}
                error={!!error?.message}
                fullWidth
                helperText={error?.message}
                label={t('createVisit:additionalInformation.description')}
                multiline
                rows={4}
                sx={{
                  my: 2,
                }}
              />
            )}
          />
        </Box>
        <Box>
          <Typography variant="subtitle2">
            {t('createVisit:additionalInformation.attachments')}
          </Typography>
          <Controller
            control={control}
            name="attachments"
            render={({ field: { value, onChange } }) => (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <FileDropzone
                  title={t('createVisit:additionalInformation.browseOrDrop')}
                  description={t('createVisit:additionalInformation.eachFileShould')}
                  maxSize={MAX_FILE_SIZE_IN_BYTES}
                  accept={{
                    'image/jpeg': ['.jpeg'],
                    'image/png': ['.png'],
                    'application/pdf': ['.pdf'],
                  }}
                  onDrop={(acceptedFiles, fileRejections) => {
                    if (
                      fileRejections?.length > 0 &&
                      acceptedFiles?.length <= 0 &&
                      fileRejections?.[0]?.errors?.[0]?.code === 'file-invalid-type'
                    ) {
                      setError('attachments', { message: t('common:incorrectFileType') });
                      return;
                    }

                    onChange([...value, ...acceptedFiles]);
                  }}
                />
                <Box>
                  {value?.length > 0 ? (
                    <FilesList
                      files={value ?? []}
                      onDeleteFile={index => {
                        const newFiles = value.filter((_, i) => i !== index);
                        onChange(newFiles);
                      }}
                    />
                  ) : null}
                  {errors.attachments ? (
                    <Typography color="error.main" variant="subtitle2">
                      <>{errors.attachments}</>
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            )}
          />
        </Box>
        {Boolean(error) && <FormHelperText error>{error}</FormHelperText>}
        <Box sx={{ mt: 'auto', pt: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              color="primary"
              onClick={() => send({ type: 'BACK' })}
              size="large"
              sx={{ mr: 2 }}
              type="button"
              variant="text">
              {t('common:button.back')}
            </Button>
            <Button
              color="primary"
              size="large"
              type="button"
              variant="contained"
              disabled={!isValid}
              onClick={nextStep}>
              {t('common:button.nextStep')}
            </Button>
          </Box>
        </Box>
      </Box>
    </div>
  );
};
